import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  Sparkles, 
  Info,
  Clock,
  Zap
} from "lucide-react";
import { SuperAdminLayout } from "@/components/admin/SuperAdminLayout";
import { useAuth } from "@/hooks/useAuth";
import { UserRole } from "@/lib/schema";
import { useToast } from "@/hooks/use-toast";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/lib/firebase";

export default function GraphRAGTuning() {
  const { userData } = useAuth();
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);

  // Only super admins can access this page
  if (userData?.role !== UserRole.SUPER_ADMIN) {
    return (
      <SuperAdminLayout
        title="AI Search Optimization"
        subtitle="Access Denied"
      >
        <Alert variant="destructive">
          <AlertDescription>
            You do not have permission to access this page.
          </AlertDescription>
        </Alert>
      </SuperAdminLayout>
    );
  }

  const handleFineTuning = async () => {
    setIsProcessing(true);
    try {
      // Call the Firebase function to trigger fine tuning
      const onFineTuneGraphRAG = httpsCallable(functions, 'onFineTuneGraphRAG');
      const result = await onFineTuneGraphRAG();
      
      const data = result.data as { success?: boolean; message?: string; error?: string };
      
      if (data.success) {
        toast({
          title: "Optimization Started",
          description: data.message || "The AI search system is being optimized. This process will run in the background and may take several hours to complete.",
        });
      } else {
        throw new Error(data.error || "Failed to start optimization");
      }
    } catch (error: any) {
      console.error("Error triggering fine tuning:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to start optimization. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <SuperAdminLayout
      title="AI Search Optimization"
      subtitle="Improve Aria's candidate matching accuracy"
      maxWidth="5xl"
    >
      <div className="space-y-6">
        {/* Main Card */}
        <Card className="border-2">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-br from-purple-100 to-blue-100 rounded-xl">
                <Sparkles className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <CardTitle className="text-2xl">Optimize AI Search Performance</CardTitle>
                <CardDescription className="text-base mt-1">
                  Enhance how Aria finds and matches candidates to your job requirements
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* What it does */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 space-y-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-100 rounded-lg mt-1">
                  <Zap className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg text-gray-900 mb-2">
                    What does this do?
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    This optimization process improves Aria's ability to understand and match candidates to your jobs. 
                    It analyzes all candidate profiles and job descriptions in your system to create better connections 
                    between skills, experience, and requirements.
                  </p>
                </div>
              </div>
            </div>

            {/* When to use */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 space-y-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-green-100 rounded-lg mt-1">
                  <Info className="h-5 w-5 text-green-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg text-gray-900 mb-2">
                    When should you run this?
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">•</span>
                      <span>After adding a large number of new candidates to the system</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">•</span>
                      <span>When you notice search results aren't as accurate as expected</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">•</span>
                      <span>After making significant updates to job descriptions or requirements</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Important notice */}
            <Alert className="border-orange-200 bg-orange-50">
              <Clock className="h-5 w-5 text-orange-600" />
              <AlertDescription className="text-orange-900 ml-2">
                <span className="font-semibold">Please note:</span> This optimization process can take several hours to complete 
                depending on the size of your candidate database. The task will run in the background, so you can continue 
                using the platform normally while it's processing.
              </AlertDescription>
            </Alert>

            {/* Action Button */}
            <div className="pt-4">
              <Button 
                variant="primary"
                onClick={handleFineTuning} 
                disabled={isProcessing}
                size="lg"
                className="w-full sm:w-auto gap-2 text-base px-8"
              >
                {isProcessing ? (
                  <>
                    <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Starting Optimization...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-5 w-5" />
                    Trigger Fine Tuning
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </SuperAdminLayout>
  );
}

